/** 结构化数据与绝对 URL 的构造。JSON-LD 只描述站内确实存在的信息，不虚构资质。 */
import { BRAND, SITE } from '@/content/site';

export const ORG_ID = `${SITE.url}/#organization`;

/** 把站内路径补成绝对 URL，已是绝对地址的原样返回。 */
export function absoluteUrl(path: string): string {
  if (/^[a-z]+:\/\//i.test(path)) return path;
  const base = SITE.url.replace(/\/+$/, '');
  if (path === '' || path === '/') return `${base}/`;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

export function organizationJsonLd() {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: BRAND.name,
    url: SITE.url,
    description: SITE.pages[0]?.description,
    email: BRAND.email,
    sameAs: [BRAND.github],
    logo: absoluteUrl('/favicon.svg'),
  };
}

export function websiteJsonLd() {
  return {
    '@type': 'WebSite',
    '@id': `${SITE.url}/#website`,
    url: SITE.url,
    name: `${BRAND.name}™`,
    inLanguage: SITE.locale,
    publisher: { '@id': ORG_ID },
  };
}

export function webPageJsonLd(input: { path: string; title: string; description: string }) {
  return {
    '@type': 'WebPage',
    '@id': `${absoluteUrl(input.path)}#webpage`,
    url: absoluteUrl(input.path),
    name: input.title,
    description: input.description,
    inLanguage: SITE.locale,
    isPartOf: { '@id': `${SITE.url}/#website` },
    about: { '@id': ORG_ID },
  };
}

export function articleJsonLd(input: {
  slug: string;
  title: string;
  description: string;
  date: string;
  tags: readonly string[];
}) {
  const url = absoluteUrl(`/news/${input.slug}`);
  return {
    '@type': 'TechArticle',
    '@id': `${url}#article`,
    url,
    headline: input.title,
    description: input.description,
    datePublished: input.date,
    dateModified: input.date,
    inLanguage: SITE.locale,
    keywords: input.tags.join('、'),
    author: { '@type': 'Organization', name: BRAND.name, url: SITE.url },
    publisher: { '@id': ORG_ID },
    isPartOf: { '@id': `${SITE.url}/#website` },
    mainEntityOfPage: { '@id': `${url}#webpage` },
  };
}

export function breadcrumbJsonLd(items: readonly { name: string; path: string }[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

/** 组装成 `<script type="application/ld+json">` 需要的字符串。 */
export function jsonLdScript(graph: readonly object[]): string {
  return JSON.stringify({ '@context': 'https://schema.org', '@graph': graph });
}
